// Shared money math for the checkout flow (LogPaymentDialog) and the
// appointment-details totals. Scheduled totals, the checkout screen, the saved
// payment row, and the post-checkout appointment rows must all agree; keeping
// the math here (and out of the components) lets the same functions be
// exercised directly in tests with real production data.

// appointments.add_ons is jsonb, but be defensive: some clients have rows where
// it round-tripped as a JSON string, and a non-array must never crash a total.
export const parseAddOns = (raw) => {
	if (Array.isArray(raw)) return raw
	if (typeof raw === 'string' && raw.trim()) {
		try {
			const parsed = JSON.parse(raw)
			return Array.isArray(parsed) ? parsed : []
		} catch {
			return []
		}
	}
	return []
}

// Entries store `price` (appointment rows) or `amount` (payment rows).
export const addOnEntryPrice = (entry) => Number(entry?.price ?? entry?.amount) || 0

// Sum of an appointment's stored add-ons at their stored prices.
export const storedAddOnsTotal = (appointmentsList) =>
	appointmentsList.reduce(
		(sum, appt) => sum + parseAddOns(appt.add_ons).reduce((s, e) => s + addOnEntryPrice(e), 0),
		0
	)

/**
 * Split the add-ons already stored on the appointment row(s) into:
 *   - counts:  occurrences per catalog add-on id, for the checkout picker
 *   - legacy:  entries that no longer match the add-on catalog (deleted
 *              service, is_add_on turned off, or missing service_id). These
 *              must still be charged at their stored price — silently dropping
 *              them is how checkout totals drift below the scheduled total.
 */
export const splitStoredAddOns = (appointmentsList, catalog) => {
	const counts = {}
	const legacy = []
	appointmentsList.forEach((appt) => {
		parseAddOns(appt.add_ons).forEach((entry, i) => {
			const match = entry?.service_id && catalog.find((a) => a.id === entry.service_id)
			if (match) {
				counts[match.id] = (counts[match.id] || 0) + 1
			} else {
				legacy.push({
					key: `${appt.id}:${i}`,
					appointmentId: appt.id,
					service_id: entry?.service_id || null,
					name: entry?.name || 'Add-on',
					price: addOnEntryPrice(entry),
					duration_minutes: entry?.duration_minutes || 0,
				})
			}
		})
	})
	return { counts, legacy }
}

const catalogEntry = (addon) => ({
	service_id: addon.id,
	name: addon.name,
	price: addon.price,
	duration_minutes: addon.duration_minutes || 0,
})

/**
 * Distribute the checkout's final add-on selection back onto the appointment
 * rows WITHOUT duplication. Historically the whole selection was written to
 * every appointment in a group, so a $30 selection on a 5-dog group became
 * $150 of add-ons the next time anything summed the rows.
 *
 * Rules:
 *   - each appointment keeps its own still-selected add-ons (in row order,
 *     while the selection count for that add-on lasts),
 *   - legacy entries stay on the row they came from,
 *   - anything newly added at checkout lands exactly once, on the primary
 *     appointment.
 *
 * Invariant: the union of the returned lists equals the checkout selection
 * (counts at catalog price + kept legacy), so any view that sums the rows
 * agrees with the payment row.
 *
 * @returns {Object} appointmentId -> add-on entry list for that row
 */
export const distributeAddOns = ({ appointmentsList, catalog, counts, legacy, primaryId }) => {
	const byAppointment = {}
	appointmentsList.forEach((appt) => { byAppointment[appt.id] = [] })
	const remaining = { ...counts }

	appointmentsList.forEach((appt) => {
		parseAddOns(appt.add_ons).forEach((entry) => {
			const match = entry?.service_id && catalog.find((a) => a.id === entry.service_id)
			if (!match) return // legacy entries are re-attached below
			if ((remaining[match.id] || 0) > 0) {
				remaining[match.id] -= 1
				byAppointment[appt.id].push(catalogEntry(match))
			}
		})
	})

	legacy.forEach((l) => {
		const target = byAppointment[l.appointmentId] ? l.appointmentId : primaryId
		byAppointment[target].push({
			service_id: l.service_id,
			name: l.name,
			price: l.price,
			duration_minutes: l.duration_minutes || 0,
		})
	})

	Object.entries(remaining).forEach(([id, count]) => {
		const match = catalog.find((a) => a.id === id)
		if (!match) return
		for (let i = 0; i < count; i++) byAppointment[primaryId].push(catalogEntry(match))
	})

	return byAppointment
}
